/**
 * Visual wrapper around an automation-authored message body.
 *
 * Promotes plain agent text into a "card" so users can tell at a
 * glance which messages came from automations and which apps were
 * involved. Three sections:
 *
 *   1. Header strip — small Pipedream app logos + "Automation" caption.
 *      Sole automation marker on the row (the username header no longer
 *      carries a duplicate pill), so the card stays self-explanatory
 *      when read out of context (search, deep links, etc.).
 *   2. Body — the existing markdown-rendered children, untouched.
 *      Markdown formatting (bold, lists, code, mentions, channel
 *      references) keeps working because we render the content tree
 *      verbatim — we just frame it.
 *   3. Action footer — every distinct URL in the message body promoted
 *      into a button. The button carries the matching app's logo when
 *      we can resolve the hostname against `apps_used` and a smart
 *      verb label ("Open dashboard", "View issue", "Visit PostHog")
 *      derived from the URL path. See `extract-action-urls.ts`.
 *
 * Footer renders nothing when there are zero URLs, so plain "the
 * count is X" digests don't grow extra chrome. Card itself stays
 * visible regardless — the colored stripe is the affordance.
 */
import type { ReactNode } from "react";
import { ArrowSquareOut } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { openExternalUrl } from "@/lib/open-url";
import { AppLogo } from "@/components/automations/AppLogo";
import { extractActionUrls, type ActionUrl } from "./extract-action-urls";

interface Props {
  /**
   * Raw markdown content — used to extract the URL list. Kept
   * separate from `children` because the children are a React node
   * tree (already rendered), and we can't grep `<a>` hrefs back out
   * cleanly post-render.
   */
  content: string;
  /** Pipedream apps the source automation touched. */
  apps?: ReadonlyArray<{ slug: string; name?: string; img_src?: string }>;
  /** Rendered markdown body, rendered as-is inside the card. */
  children: ReactNode;
}

/** Map app slug → {name, img_src} so action buttons can render logos. */
function appsBySlug(
  apps: Props["apps"],
): Map<string, { name?: string; img_src?: string }> {
  const map = new Map<string, { name?: string; img_src?: string }>();
  for (const a of apps ?? []) {
    map.set(a.slug, { name: a.name, img_src: a.img_src });
  }
  return map;
}

/**
 * Cap the action footer at this many buttons. Beyond this we'd start
 * fighting the body for vertical space — and any digest with >5 links
 * is better served by the inline markdown links the user can scroll
 * through. The cap matches the AppLogoStrip overflow heuristic.
 */
const MAX_ACTION_BUTTONS = 5;

export function AutomationMessageCard({ content, apps, children }: Props) {
  const allUrls = extractActionUrls(content);
  const visibleUrls = allUrls.slice(0, MAX_ACTION_BUTTONS);
  const overflowCount = allUrls.length - visibleUrls.length;
  const slugMap = appsBySlug(apps);
  const hasApps = (apps?.length ?? 0) > 0;

  return (
    <div className="mt-1 w-full max-w-[34rem] overflow-hidden rounded-md bg-primary/5">
      {/* Always render the caption strip — it's the at-a-glance signal
          that this row was auto-posted. Apps render inline after when
          metadata.apps_used is present. */}
      <div className="flex items-center gap-1.5 border-b border-border/50 px-3 py-1.5 text-[11px] text-muted-foreground">
        <span className="text-[10px] font-medium uppercase tracking-wide">
          Automation
        </span>
        {hasApps && (
          <>
            <span aria-hidden="true" className="text-muted-foreground/60">
              ·
            </span>
            <span className="flex min-w-0 items-center gap-1.5 text-foreground/80">
              {apps!.slice(0, 4).map((a) => (
                <span key={a.slug} className="inline-flex items-center gap-1">
                  <AppLogo
                    slug={a.slug}
                    imgSrc={a.img_src}
                    name={a.name}
                    size={14}
                  />
                  <span className="font-medium">{a.name ?? a.slug}</span>
                </span>
              ))}
            </span>
          </>
        )}
      </div>
      <div className="px-3 py-2">{children}</div>
      {visibleUrls.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
          {visibleUrls.map((url) => (
            <ActionButton key={url.url} action={url} appBySlug={slugMap} />
          ))}
          {overflowCount > 0 && (
            <span className="text-[11px] text-muted-foreground">
              +{overflowCount} more in message
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Single action button. Uses the app logo (when the URL's slug
 * matches an entry from `apps_used`) so the button reads visually
 * before the user reads the label. Falls back to a generic external-
 * link icon for unknown hosts.
 */
function ActionButton({
  action,
  appBySlug,
}: {
  action: ActionUrl;
  appBySlug: Map<string, { name?: string; img_src?: string }>;
}) {
  // Prefer the message's `apps_used` metadata for icon — that's the
  // authoritative resolved name + CDN URL. If the URL points to an
  // app the automation didn't declare in its spec (rare, but possible
  // when the agent appends an unrelated link), we still know the
  // slug from the hostname; AppLogo renders a letter-circle fallback.
  const appMeta = action.slug ? appBySlug.get(action.slug) : undefined;
  return (
    <Button
      type="button"
      variant="outline"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        openExternalUrl(action.url);
      }}
      title={action.url}
      className="h-auto gap-2 border-primary/30 bg-background/60 px-2.5 py-1.5 text-xs font-medium text-primary hover:border-primary/60 hover:bg-primary/10"
    >
      {action.slug ? (
        <AppLogo
          slug={action.slug}
          imgSrc={appMeta?.img_src}
          name={appMeta?.name ?? action.slug}
          size={14}
        />
      ) : (
        <ArrowSquareOut size={12} />
      )}
      <span>{action.label}</span>
    </Button>
  );
}
